import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import { encrypt } from "../../utils/encryption";
import { formatDate } from "../../utils/dateUtils";
import alertService from "../../utils/alertService";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import DatePicker from "../../components/common/DatePicker";
import TextArea from "../../components/common/TextArea";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import ErrorAlert from "../../components/common/ErrorAlert";
import salesPaymentService from "../../services/salesPaymentService";
import paymentModeService from "../../services/paymentModeService";
import { API_CONFIG } from "../../utils/apiConfig";

function getTodayDateString() {
  const today = new Date();
  const year = String(today.getFullYear());
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function SalesPaymentDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: sellingMasterId } = useEncryptedParams();
  const basePath = "/sales-payment-master";

  // --- States ---
  const [sellingMasterData, setSellingMasterData] = useState(
    location.state?.sellingMaster || null,
  );
  const [payments, setPayments] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmittingTransaction, setIsSubmittingTransaction] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // Form State for Payment Transaction (Add/Edit)
  const [isEditingTransaction, setIsEditingTransaction] = useState(false);
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [transactionForm, setTransactionForm] = useState({
    amount: "",
    date: getTodayDateString(),
    paymentModeId: "",
    gstAmount: "",
    tdsAmount: "",
    netAmount: "",
    remarks: "",
    isActive: true,
    bankName: "",
    accountNo: "",
    ifscCode: "",
    transactionRefNo: "",
    vpa: "",
    chequeNo: "",
    chequeDate: getTodayDateString(),
    attachment: null,
    attachmentPath: ""
  });

  // Load Selling Master and Payments
  const loadAllData = useCallback(async () => {
    if (!sellingMasterId) return;
    setIsLoading(true);
    setError(null);
    try {
      // 1. Fetch payments
      const paymentsData =
        await salesPaymentService.getPaymentsBySellingMasterId(sellingMasterId);
      setPayments(paymentsData);

      // 2. Fetch/resolve Selling Master details if not in state
      if (!sellingMasterData) {
        const response = await salesPaymentService.getSalesPayments({
          limit: 1000,
        });
        const matched = (response.items || []).find(
          (item) => String(item.sellingMasterId) === String(sellingMasterId),
        );
        if (matched) {
          setSellingMasterData(matched);
        } else {
          setError("Selling Master record not found.");
        }
      } else {
        // Refresh received/remaining values from fresh payments list
        const newReceived = paymentsData.reduce(
          (sum, p) => sum + Number(p.amount),
          0,
        );
        setSellingMasterData((prev) => ({
          ...prev,
          receivedAmount: newReceived,
          remainingReceivableAmount: Number(
            (prev.totalAmount - newReceived).toFixed(2),
          ),
        }));
      }
    } catch (err) {
      setError(err.message || "Failed to load data.");
    } finally {
      setIsLoading(false);
    }
  }, [sellingMasterId, sellingMasterData]);

  // Load Payment Modes
  useEffect(() => {
    const loadModes = async () => {
      try {
        const modes = await paymentModeService.getActivePaymentModes();
        setPaymentModes(
          modes.map((m) => ({
            label: m.paymentMode,
            value: String(m.id),
            systemKey: m.systemKey
          })),
        );
      } catch (err) {
        console.error("Failed to load payment modes", err);
      }
    };
    loadModes();
  }, []);

  // Initial Load
  useEffect(() => {
    if (!sellingMasterId) {
      navigate(basePath);
      return;
    }
    loadAllData();
  }, [sellingMasterId]);

  // Reset Form
  const resetTransactionForm = () => {
    setTransactionForm({
      amount: "",
      date: getTodayDateString(),
      paymentModeId: "",
      gstAmount: "",
      tdsAmount: "",
      netAmount: "",
      remarks: "",
      isActive: true,
      bankName: "",
      accountNo: "",
      ifscCode: "",
      transactionRefNo: "",
      vpa: "",
      chequeNo: "",
      chequeDate: getTodayDateString(),
      attachment: null,
      attachmentPath: ""
    });
    setFormErrors({});
    setIsEditingTransaction(false);
    setEditingTransactionId(null);
    const fileInput = document.getElementById("payment-attachment-input");
    if (fileInput) fileInput.value = "";
  };

  // Recompute netAmount from amount, tdsAmount, gstAmount (all manual except netAmount)
  const recomputeNetAmount = (amount, tdsAmount, gstAmount) => {
    const amt = parseFloat(amount) || 0;
    const tds = parseFloat(tdsAmount) || 0;
    const gst = parseFloat(gstAmount) || 0;
    const netAmt = Number((amt - tds - gst).toFixed(2));
    return amt > 0 ? String(netAmt < 0 ? 0 : netAmt) : "";
  };

  // Handle Form Input Changes
  const handleFormChange = (field, value) => {
    let val = value;
    if (field === "attachment" && val) {
      const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
      const maxSize = 5 * 1024 * 1024; // 5 MB
      if (!allowedTypes.includes(val.type)) {
        alertService.error("Only PDF, JPG, and PNG files are allowed.");
        val = null;
        const fileInput = document.getElementById("payment-attachment-input");
        if (fileInput) fileInput.value = "";
      } else if (val.size > maxSize) {
        alertService.error("Maximum file size allowed is 5 MB.");
        val = null;
        const fileInput = document.getElementById("payment-attachment-input");
        if (fileInput) fileInput.value = "";
      }
    }
    if (field === "chequeNo") {
      val = val.replace(/[^a-zA-Z0-9]/g, "");
      if (val.length > 10) {
        val = val.slice(0, 10);
      }
    }
    if (field === "vpa") {
      // Remove spaces and any characters that are not alphanumeric, @, ., -, or _
      val = val.replace(/[^a-zA-Z0-9@.\-_]/g, "");
      // Keep only the first @ and strip any subsequent @
      const atCount = (val.match(/@/g) || []).length;
      if (atCount > 1) {
        const firstAtIdx = val.indexOf("@");
        val = val.slice(0, firstAtIdx + 1) + val.slice(firstAtIdx + 1).replace(/@/g, "");
      }
    }
    if (field === "accountNo") {
      val = val.replace(/[^a-zA-Z0-9]/g, "");
      if (val.length > 20) {
        val = val.slice(0, 20);
      }
    }
    if (field === "bankName" && val.length > 80) {
      val = val.slice(0, 80);
    }
    if (field === "ifscCode") {
      val = val.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
      if (val.length > 14) {
        val = val.slice(0, 14);
      }
    }
    setTransactionForm((prev) => {
      const updated = { ...prev, [field]: val };
      if (["amount", "tdsAmount", "gstAmount"].includes(field)) {
        return {
          ...updated,
          netAmount: recomputeNetAmount(
            field === "amount" ? val : updated.amount,
            field === "tdsAmount" ? val : updated.tdsAmount,
            field === "gstAmount" ? val : updated.gstAmount,
          ),
        };
      }
      return updated;
    });
    setFormErrors((prev) => ({ ...prev, [field]: null }));
  };

  const GST_RATE = 18; // 18%
  const TDS_RATE = 0.1; // 0.1%

  const handleAmountChange = (value) => {
    let sanitizedVal = value;
    if (sanitizedVal && sanitizedVal.includes("-")) {
      sanitizedVal = sanitizedVal.replace(/-/g, "");
    }
    if (sanitizedVal && sanitizedVal.includes(".")) {
      const parts = sanitizedVal.split(".");
      if (parts[1] && parts[1].length > 2) {
        sanitizedVal = `${parts[0]}.${parts[1].slice(0, 2)}`;
      }
    }
    // Clamp to remaining balance
    const currentReceived = payments.reduce((sum, p) => {
      if (isEditingTransaction && p.id === editingTransactionId) return sum;
      return sum + Number(p.amount);
    }, 0);
    const maxAllowed = Number(
      ((Number(sellingMasterData?.totalAmount) || 0) - currentReceived).toFixed(
        2,
      ),
    );

    const numVal = parseFloat(sanitizedVal) || 0;
    const clampedValue = numVal > maxAllowed ? String(maxAllowed) : sanitizedVal;
    const amt = parseFloat(clampedValue) || 0;

    // Auto-calculate GST (18%) and TDS (0.1%)
    const gstAmt = amt > 0 ? Number(((amt * GST_RATE) / 100).toFixed(2)) : 0;
    const tdsAmt = amt > 0 ? Number(((amt * TDS_RATE) / 100).toFixed(2)) : 0;
    const netAmt = amt > 0 ? Number((amt - gstAmt - tdsAmt).toFixed(2)) : 0;

    setTransactionForm((prev) => ({
      ...prev,
      amount: clampedValue,
      gstAmount: amt > 0 ? String(gstAmt) : "",
      tdsAmount: amt > 0 ? String(tdsAmt) : "",
      netAmount: amt > 0 ? String(netAmt < 0 ? 0 : netAmt) : "",
    }));

    if (numVal > maxAllowed) {
      setFormErrors((prev) => ({
        ...prev,
        amount: `Cannot exceed remaining balance of ₹${maxAllowed.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
      }));
    } else {
      setFormErrors((prev) => ({ ...prev, amount: null }));
    }
  };

  // Handle Edit Transaction
  const handleEditTransaction = (transaction) => {
    setIsEditingTransaction(true);
    setEditingTransactionId(transaction.id);
    setTransactionForm({
      amount: String(transaction.amount),
      date: transaction.date
        ? transaction.date.split("T")[0]
        : getTodayDateString(),
      paymentModeId: String(transaction.paymentModeId),
      gstAmount:
        transaction.gstAmount !== null && transaction.gstAmount !== undefined
          ? String(transaction.gstAmount)
          : "",
      tdsAmount:
        transaction.tdsAmount !== null && transaction.tdsAmount !== undefined
          ? String(transaction.tdsAmount)
          : "",
      netAmount:
        transaction.netAmount !== null && transaction.netAmount !== undefined
          ? String(transaction.netAmount)
          : "",
      remarks: transaction.remarks || transaction.remark || "",
      isActive: Boolean(transaction.isActive),
      bankName: transaction.bankName || "",
      accountNo: transaction.accountNo || "",
      ifscCode: transaction.ifscCode || "",
      transactionRefNo: transaction.transactionRefNo || "",
      vpa: transaction.vpa || "",
      chequeNo: transaction.chequeNo || "",
      chequeDate: transaction.chequeDate
        ? transaction.chequeDate.split("T")[0]
        : getTodayDateString(),
      attachment: null,
      attachmentPath: transaction.attachmentPath || ""
    });
    setFormErrors({});
  };

  // Handle Delete Transaction
  const handleDeleteTransaction = async (transaction) => {
    const selectedMode = transaction.paymentMode;
    const modeLabel = selectedMode ? selectedMode.paymentMode : `Mode #${transaction.paymentModeId}`;
    const sysKey = selectedMode ? selectedMode.systemKey : "";

    let paymentDetailsStr = "";
    if (sysKey === "bank") {
      paymentDetailsStr = `Bank: ${transaction.bankName || "N/A"} | UTR: ${transaction.transactionRefNo || "N/A"}`;
    } else if (sysKey === "upi") {
      paymentDetailsStr = `UPI: ${transaction.vpa || "N/A"} | ID: ${transaction.transactionRefNo || "N/A"}`;
    } else if (sysKey === "cheque") {
      paymentDetailsStr = `Cheque: #${transaction.chequeNo || "N/A"} | Date: ${transaction.chequeDate ? formatDate(transaction.chequeDate) : "N/A"}`;
    } else if (sysKey === "cash") {
      paymentDetailsStr = `Cash | Bank: ${transaction.bankName || "N/A"} | A/C: ${transaction.accountNo || "N/A"}`;
    } else if (transaction.bankName || transaction.accountNo) {
      paymentDetailsStr = `Bank: ${transaction.bankName || "N/A"} | A/C: ${transaction.accountNo || "N/A"}`;
    }

    const details = {
      date: formatDate(transaction.date),
      paymentMode: modeLabel,
      formattedAmount: `₹${Number(transaction.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
      gstAmount: transaction.gstAmount ? `₹${Number(transaction.gstAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : null,
      tdsAmount: transaction.tdsAmount ? `₹${Number(transaction.tdsAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : null,
      netAmount: transaction.netAmount ? `₹${Number(transaction.netAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : null,
      paymentDetails: paymentDetailsStr || null,
      remarks: transaction.remarks || transaction.remark || null
    };

    const confirm = await alertService.confirmPaymentAction(details, "delete");
    if (!confirm.isConfirmed) return;

    try {
      await salesPaymentService.deleteSalesPayment(transaction.id);
      alertService.success("Payment transaction deleted successfully.");

      // Reload payments
      const updatedPayments =
        await salesPaymentService.getPaymentsBySellingMasterId(sellingMasterId);
      setPayments(updatedPayments);

      // Update local stats
      const newReceived = updatedPayments.reduce(
        (sum, p) => sum + Number(p.amount),
        0,
      );
      setSellingMasterData((prev) => ({
        ...prev,
        receivedAmount: newReceived,
        remainingReceivableAmount: Number(
          (prev.totalAmount - newReceived).toFixed(2),
        ),
      }));
    } catch (err) {
      alertService.error(err.message || "Failed to delete transaction.");
    }
  };

  // Handle Submit
  const handleSubmitTransaction = async (e) => {
    e.preventDefault();
    setFormErrors({});

    const errors = {};
    if (!transactionForm.amount || parseFloat(transactionForm.amount) <= 0) {
      errors.amount = "Amount must be greater than 0";
    }
    if (!transactionForm.date) {
      errors.date = "Date is required";
    } else if (sellingMasterData && sellingMasterData.date) {
      const paymentDate = new Date(transactionForm.date);
      const invoiceDate = new Date(sellingMasterData.date);
      paymentDate.setHours(0, 0, 0, 0);
      invoiceDate.setHours(0, 0, 0, 0);
      if (paymentDate < invoiceDate) {
        errors.date = "Payment date cannot be before the invoice date";
      }
    }
    if (!transactionForm.paymentModeId) {
      errors.paymentModeId = "Payment Mode is required";
    }

    // Validate limit
    if (sellingMasterData && transactionForm.amount) {
      const amountNum = parseFloat(transactionForm.amount);
      const currentReceived = payments.reduce((sum, p) => {
        if (isEditingTransaction && p.id === editingTransactionId) return sum;
        return sum + Number(p.amount);
      }, 0);

      const maxAllowed = Number(
        (sellingMasterData.totalAmount - currentReceived).toFixed(2),
      );
      if (amountNum > maxAllowed) {
        errors.amount = `Amount cannot exceed the remaining receivable limit of ₹${maxAllowed.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
      }
    }

    const selectedMode = paymentModes.find(m => String(m.value) === String(transactionForm.paymentModeId));
    const sysKey = selectedMode ? selectedMode.systemKey : "";
    const modeLabel = selectedMode ? selectedMode.label : "N/A";

    // --- Bank Name: Mandatory for ALL modes ---
    if (!transactionForm.bankName?.trim()) {
      errors.bankName = "Bank Name is required";
    } else if (transactionForm.bankName.length > 80) {
      errors.bankName = "Bank name cannot exceed 80 characters";
    }

    // --- Cheque-specific mandatory fields ---
    if (sysKey === "cheque") {
      if (!transactionForm.chequeNo?.trim()) {
        errors.chequeNo = "Cheque Number is required";
      } else if (/[^a-zA-Z0-9]/.test(transactionForm.chequeNo)) {
        errors.chequeNo = "Cheque number cannot contain special characters";
      } else if (transactionForm.chequeNo.length > 10) {
        errors.chequeNo = "Cheque number cannot exceed 10 characters";
      }
      if (!transactionForm.chequeDate) {
        errors.chequeDate = "Cheque Date is required";
      }
    }

    // --- Bank (NEFT/RTGS/IMPS)-specific mandatory fields ---
    if (sysKey === "bank") {
      if (!transactionForm.accountNo?.trim()) {
        errors.accountNo = "Account Number is required";
      } else if (/[^a-zA-Z0-9]/.test(transactionForm.accountNo)) {
        errors.accountNo = "Account number cannot contain spaces or special characters";
      } else if (transactionForm.accountNo.length > 20) {
        errors.accountNo = "Account number cannot exceed 20 characters";
      }
      if (!transactionForm.ifscCode?.trim()) {
        errors.ifscCode = "IFSC Code is required";
      } else if (/[^a-zA-Z0-9]/.test(transactionForm.ifscCode)) {
        errors.ifscCode = "IFSC code cannot contain spaces or special characters";
      } else if (transactionForm.ifscCode.length > 14) {
        errors.ifscCode = "IFSC code cannot exceed 14 characters";
      }
      if (!transactionForm.transactionRefNo?.trim()) {
        errors.transactionRefNo = "UTR / Reference Number is required";
      }
    }

    // --- UPI-specific mandatory fields ---
    if (sysKey === "upi") {
      const vpaVal = transactionForm.vpa?.trim();
      if (!vpaVal) {
        errors.vpa = "UPI ID (VPA) is required";
      } else {
        const upiRegex = /^[a-zA-Z0-9.\-_]+@[a-zA-Z0-9.\-_]+$/;
        if (!upiRegex.test(vpaVal)) {
          errors.vpa = "Invalid UPI ID format (e.g., username@bank)";
        }
      }
      if (!transactionForm.transactionRefNo?.trim()) {
        errors.transactionRefNo = "Transaction ID is required";
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const payload = {
      sellingMasterId: String(sellingMasterId),
      amount: Number(transactionForm.amount),
      date: new Date(transactionForm.date).toISOString(),
      paymentModeId: String(transactionForm.paymentModeId),
      netAmount:
        transactionForm.netAmount !== ""
          ? Number(transactionForm.netAmount)
          : 0,
      gstAmount:
        transactionForm.gstAmount !== ""
          ? Number(transactionForm.gstAmount)
          : 0,
      tdsAmount:
        transactionForm.tdsAmount !== ""
          ? Number(transactionForm.tdsAmount)
          : 0,
      remarks: transactionForm.remarks?.trim() || "",
      isActive: true,
      // Dynamic fields mapping per payment mode
      bankName: transactionForm.bankName?.trim() || null,
      accountNo: (sysKey === "cash" || sysKey === "bank") ? transactionForm.accountNo?.trim() || null : null,
      ifscCode: (sysKey === "bank") ? transactionForm.ifscCode?.trim() || null : null,
      transactionRefNo: (sysKey === "bank" || sysKey === "upi") ? transactionForm.transactionRefNo?.trim() || null : null,
      vpa: (sysKey === "upi") ? transactionForm.vpa?.trim() || null : null,
      chequeNo: (sysKey === "cheque") ? transactionForm.chequeNo?.trim() || null : null,
      chequeDate: (sysKey === "cheque") ? new Date(transactionForm.chequeDate).toISOString() : null,
      attachment: transactionForm.attachment || null,
      attachmentPath: transactionForm.attachmentPath || ""
    };

    let paymentDetailsStr = "";
    if (sysKey === "bank") {
      paymentDetailsStr = `
        <div><strong>Bank:</strong> ${transactionForm.bankName || "N/A"}</div>
        <div><strong>A/C:</strong> ${transactionForm.accountNo || "N/A"}</div>
        <div><strong>IFSC:</strong> ${transactionForm.ifscCode || "N/A"}</div>
        <div><strong>UTR:</strong> ${transactionForm.transactionRefNo || "N/A"}</div>
      `;
    } else if (sysKey === "upi") {
      paymentDetailsStr = `
        <div><strong>UPI ID:</strong> ${transactionForm.vpa || "N/A"}</div>
        <div><strong>Txn ID:</strong> ${transactionForm.transactionRefNo || "N/A"}</div>
      `;
    } else if (sysKey === "cheque") {
      paymentDetailsStr = `
        <div><strong>Cheque:</strong> #${transactionForm.chequeNo || "N/A"}</div>
        <div><strong>Date:</strong> ${transactionForm.chequeDate ? formatDate(transactionForm.chequeDate) : "N/A"}</div>
        <div><strong>Bank:</strong> ${transactionForm.bankName || "N/A"}</div>
      `;
    } else if (sysKey === "cash") {
      paymentDetailsStr = `
        <div><strong>Cash</strong></div>
        <div><strong>Bank:</strong> ${transactionForm.bankName || "N/A"}</div>
        ${transactionForm.accountNo ? `<div><strong>A/C:</strong> ${transactionForm.accountNo}</div>` : ""}
      `;
    } else if (transactionForm.bankName || transactionForm.accountNo) {
      paymentDetailsStr = `
        <div><strong>Bank:</strong> ${transactionForm.bankName || "N/A"}</div>
        <div><strong>A/C:</strong> ${transactionForm.accountNo || "N/A"}</div>
      `;
    }

    const details = {
      date: formatDate(transactionForm.date),
      paymentMode: modeLabel,
      formattedAmount: `₹${Number(transactionForm.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
      paymentDetails: paymentDetailsStr || null,
      remarks: transactionForm.remarks?.trim() || null
    };

    const actionType = isEditingTransaction ? "update" : "save";
    const confirm = await alertService.confirmPaymentAction(details, actionType);
    if (!confirm.isConfirmed) return;

    setIsSubmittingTransaction(true);
    try {
      if (isEditingTransaction) {
        await salesPaymentService.updateSalesPayment(
          editingTransactionId,
          payload,
        );
        alertService.success("Payment transaction updated successfully.");
      } else {
        await salesPaymentService.createSalesPayment(payload);
        alertService.success("Payment transaction recorded successfully.");
      }
      resetTransactionForm();

      // Reload payments
      const updatedPayments =
        await salesPaymentService.getPaymentsBySellingMasterId(sellingMasterId);
      setPayments(updatedPayments);

      // Update local Selling Master stats
      const newReceived = updatedPayments.reduce(
        (sum, p) => sum + Number(p.amount),
        0,
      );
      setSellingMasterData((prev) => ({
        ...prev,
        receivedAmount: newReceived,
        remainingReceivableAmount: Number(
          (prev.totalAmount - newReceived).toFixed(2),
        ),
      }));
    } catch (err) {
      alertService.error(err.message || "Failed to save payment transaction.");
    } finally {
      setIsSubmittingTransaction(false);
    }
  };

  const getEffectivePath = () => {
    if (
      location.pathname.includes("/invoice-management/sales-payment-master")
    ) {
      return "/invoice-management/sales-payment-master";
    }
    return basePath;
  };

  if (isLoading && !sellingMasterData) {
    return (
      <div className="p-10 text-center text-slate-500">
        Loading payment details...
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-8 px-4 sm:px-0">
      {/* --- Header --- */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between border-b border-slate-200 dark:border-slate-800 pb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <button
              onClick={() => navigate(getEffectivePath())}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 transition-colors"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <h1 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">
              Manage Sales <span className="text-primary-600">Payments</span>
            </h1>
          </div>
          {sellingMasterData && (
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-y-1.5 gap-x-3 text-xs font-bold text-slate-500 dark:text-slate-400 md:pl-9 uppercase tracking-widest">
              <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 w-fit">
                Invoice No: {sellingMasterData.invoiceNo || "N/A"}
              </span>
              <span className="hidden sm:inline">•</span>
              <span className="text-primary-600">
                Customer Name: {sellingMasterData.customerName || "N/A"}
              </span>
              {sellingMasterData.companyName && (
                <>
                  <span className="hidden sm:inline">•</span>
                  <span>
                    Company Name: {sellingMasterData.companyName}
                  </span>
                </>
              )}
              <span className="hidden sm:inline">•</span>
              <span>Date: {formatDate(sellingMasterData.date)}</span>
              {sellingMasterData.taxType && (
                <>
                  <span className="hidden sm:inline">•</span>
                  <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 dark:bg-amber-950/20 dark:text-amber-400 border border-amber-200 dark:border-amber-900 text-[10px] w-fit">
                    Tax Type: {sellingMasterData.taxType}
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {error && <ErrorAlert message={error} />}

      {sellingMasterData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {/* Card 1: Total Receivable */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                  Total Receivable
                </p>
                <h3 className="text-xl font-black text-slate-800 dark:text-white font-mono">
                  ₹
                  {Number(sellingMasterData.totalAmount || 0).toLocaleString(
                    "en-IN",
                    { minimumFractionDigits: 2 },
                  )}
                </h3>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 text-slate-500 dark:bg-slate-800">
                <i className="material-icons text-xl">account_balance_wallet</i>
              </div>
            </div>

            {/* Card 2: Total Received */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                  Total Received
                </p>
                <h3 className="text-xl font-black text-emerald-600 dark:text-emerald-400 font-mono">
                  ₹
                  {Number(sellingMasterData.receivedAmount || 0).toLocaleString(
                    "en-IN",
                    { minimumFractionDigits: 2 },
                  )}
                </h3>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400">
                <i className="material-icons text-xl">check_circle</i>
              </div>
            </div>

            {/* Card 3: Remaining Balance */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                  Remaining Balance
                </p>
                <h3
                  className={`text-xl font-black font-mono ${Number(sellingMasterData.remainingReceivableAmount || 0) <= 0 ? "text-slate-400" : "text-amber-600"}`}
                >
                  ₹
                  {Number(
                    sellingMasterData.remainingReceivableAmount || 0,
                  ).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </h3>
              </div>
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-xl ${Number(sellingMasterData.remainingReceivableAmount || 0) <= 0 ? "bg-slate-50 text-slate-400" : "bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400"}`}
              >
                <i className="material-icons text-xl">pending_actions</i>
              </div>
            </div>
          </div>

          {/* Grid Split Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Side: Payment History */}
            <div className="lg:col-span-8 space-y-3">
              <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="bg-slate-50/50 dark:bg-slate-800/50 p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                  <h2 className="text-xs font-black text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-3">
                    <i className="material-icons text-sm">history</i>
                    Transaction History
                    <span className="px-2 py-0.5 rounded-full bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-[10px]">
                      {payments.length} Records
                    </span>
                  </h2>
                </div>

                <div className="hidden md:block overflow-x-auto">
                  {(() => {
                    const actionsVisible = true;
                    return (
                      <table className="w-full text-left text-xs">
                        <thead>
                          <tr className="bg-slate-50/30 dark:bg-slate-800/30 text-[10px] font-black uppercase tracking-wider text-slate-400 border-b border-slate-100 dark:border-slate-800 whitespace-nowrap">
                            <th className="px-5 py-3 text-center">Sr. No.</th>
                            <th className="px-5 py-3">Date</th>
                            <th className="px-5 py-3">Payment Mode</th>
                            <th className="px-5 py-3 text-right">
                              Amount (₹)
                            </th>
                            <th className="px-5 py-3 min-w-[250px]">Details</th>
                            <th className="px-5 py-3 text-center">Receipt</th>
                            <th className="px-5 py-3">Remarks</th>
                            {actionsVisible && (
                              <th className="px-5 py-3 text-center">Actions</th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                          {payments.length === 0 ? (
                            <tr>
                              <td
                                colSpan={actionsVisible ? "8" : "7"}
                                className="px-5 py-12 text-center text-slate-400 italic"
                              >
                                No payment transactions recorded for this
                                invoice yet.
                              </td>
                            </tr>
                          ) : (
                            payments.map((p, index) => (
                              <tr
                                key={p.id}
                                className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors"
                              >
                                <td className="px-5 py-3 text-center font-medium">
                                  {index + 1}
                                </td>
                                <td className="px-5 py-3 font-medium">
                                  {formatDate(p.date)}
                                </td>
                                <td className="px-5 py-3 whitespace-nowrap">
                                  <span className="px-2.5 py-1 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 text-[11px] font-bold whitespace-nowrap">
                                    {p.paymentMode?.paymentMode ||
                                      `Mode #${p.paymentModeId}`}
                                  </span>
                                </td>
                                <td className="px-5 py-3 text-right font-mono font-black text-slate-800 dark:text-white">
                                  ₹
                                  {Number(p.amount || 0).toLocaleString(
                                    "en-IN",
                                    { minimumFractionDigits: 2 },
                                  )}
                                </td>
                                <td className="px-5 py-3 text-slate-600 dark:text-slate-300 font-medium min-w-[250px]">
                                  {(() => {
                                    const key = p.paymentMode?.systemKey || "";
                                    if (key === "bank") {
                                      return (
                                        <div className="flex flex-col gap-0.5 text-xs py-1">
                                          <div><span className="font-bold text-slate-400 dark:text-slate-500">Bank:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.bankName || "N/A"}</span></div>
                                          <div><span className="font-bold text-slate-400 dark:text-slate-500">A/C:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.accountNo || "N/A"}</span></div>
                                          <div><span className="font-bold text-slate-400 dark:text-slate-500">IFSC:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.ifscCode || "N/A"}</span></div>
                                          <div><span className="font-bold text-slate-400 dark:text-slate-500">UTR:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.transactionRefNo || "N/A"}</span></div>
                                        </div>
                                      );
                                    } else if (key === "upi") {
                                      return (
                                        <div className="flex flex-col gap-0.5 text-xs py-1">
                                          <div><span className="font-bold text-slate-400 dark:text-slate-500">UPI:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.vpa || "N/A"}</span></div>
                                          <div><span className="font-bold text-slate-400 dark:text-slate-500">ID:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.transactionRefNo || "N/A"}</span></div>
                                        </div>
                                      );
                                    } else if (key === "cheque") {
                                      return (
                                        <div className="flex flex-col gap-0.5 text-xs py-1">
                                          <div><span className="font-bold text-slate-400 dark:text-slate-500">Cheque:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">#{p.chequeNo || "N/A"}</span></div>
                                          <div><span className="font-bold text-slate-400 dark:text-slate-500">Date:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.chequeDate ? formatDate(p.chequeDate) : "N/A"}</span></div>
                                          <div><span className="font-bold text-slate-400 dark:text-slate-500">Bank:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.bankName || "N/A"}</span></div>
                                        </div>
                                      );
                                    } else if (key === "cash") {
                                      return (
                                        <div className="flex flex-col gap-0.5 text-xs py-1">
                                          <div className="font-bold text-slate-500 dark:text-slate-400">Cash</div>
                                          {p.bankName && <div><span className="font-bold text-slate-400 dark:text-slate-500">Bank:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.bankName}</span></div>}
                                          {p.accountNo && <div><span className="font-bold text-slate-400 dark:text-slate-500">A/C:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.accountNo}</span></div>}
                                        </div>
                                      );
                                    }
                                    if (p.bankName || p.accountNo) {
                                      return (
                                        <div className="flex flex-col gap-0.5 text-xs py-1">
                                          {p.bankName && <div><span className="font-bold text-slate-400 dark:text-slate-500">Bank:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.bankName}</span></div>}
                                          {p.accountNo && <div><span className="font-bold text-slate-400 dark:text-slate-500">A/C:</span> <span className="text-slate-700 dark:text-slate-300 font-semibold">{p.accountNo}</span></div>}
                                        </div>
                                      );
                                    }
                                    return <span className="text-slate-400">N/A</span>;
                                  })()}
                                </td>
                                <td className="px-5 py-3 text-center">
                                  {p.attachmentPath ? (
                                    <a
                                      href={`${API_CONFIG.baseUrl}/${p.attachmentPath.replace(/\\/g, "/")}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 font-bold hover:underline"
                                    >
                                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                      View
                                    </a>
                                  ) : (
                                    <span className="text-slate-400">N/A</span>
                                  )}
                                </td>
                                <td className="px-5 py-3 text-slate-600 dark:text-slate-300 max-w-[200px]">
                                  <div className="max-w-[200px] overflow-x-auto whitespace-nowrap custom-scrollbar" title={p.remarks || p.remark || ""}>
                                    {p.remarks || p.remark || "N/A"}
                                  </div>
                                </td>
                                {actionsVisible && (
                                  <td className="px-5 py-3 text-center">
                                    <div className="flex items-center justify-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => handleEditTransaction(p)}
                                        className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/20 transition-all"
                                        title="Edit"
                                      >
                                        <svg
                                          className="h-4 w-4"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                          />
                                        </svg>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleDeleteTransaction(p)
                                        }
                                        className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/20 transition-all"
                                        title="Delete"
                                      >
                                        <svg
                                          className="h-4 w-4"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                          />
                                        </svg>
                                      </button>
                                    </div>
                                  </td>
                                )}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>

                {/* Mobile View: Cards */}
                <div className="md:hidden divide-y divide-slate-100 dark:divide-slate-800">
                  {payments.length === 0 ? (
                    <div className="px-5 py-8 text-center text-slate-400 italic">
                      No payment transactions recorded for this invoice yet.
                    </div>
                  ) : (
                    payments.map((p, index) => {
                      const remarksText = p.remarks || p.remark || "N/A";
                      const detailsContent = (() => {
                        const key = p.paymentMode?.systemKey || "";
                        if (key === "bank") {
                          return (
                            <div className="flex flex-col gap-0.5">
                              <div><span className="font-bold text-slate-400 dark:text-slate-500">Bank:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.bankName || "N/A"}</span></div>
                              <div><span className="font-bold text-slate-400 dark:text-slate-500">A/C:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.accountNo || "N/A"}</span></div>
                              <div><span className="font-bold text-slate-400 dark:text-slate-500">IFSC:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.ifscCode || "N/A"}</span></div>
                              <div><span className="font-bold text-slate-400 dark:text-slate-500">UTR:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.transactionRefNo || "N/A"}</span></div>
                            </div>
                          );
                        } else if (key === "upi") {
                          return (
                            <div className="flex flex-col gap-0.5">
                              <div><span className="font-bold text-slate-400 dark:text-slate-500">UPI:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.vpa || "N/A"}</span></div>
                              <div><span className="font-bold text-slate-400 dark:text-slate-500">ID:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.transactionRefNo || "N/A"}</span></div>
                            </div>
                          );
                        } else if (key === "cheque") {
                          return (
                            <div className="flex flex-col gap-0.5">
                              <div><span className="font-bold text-slate-400 dark:text-slate-500">Cheque:</span> <span className="font-medium text-slate-700 dark:text-slate-300">#{p.chequeNo || "N/A"}</span></div>
                              <div><span className="font-bold text-slate-400 dark:text-slate-500">Date:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.chequeDate ? formatDate(p.chequeDate) : "N/A"}</span></div>
                              <div><span className="font-bold text-slate-400 dark:text-slate-500">Bank:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.bankName || "N/A"}</span></div>
                            </div>
                          );
                        } else if (key === "cash") {
                          return (
                            <div className="flex flex-col gap-0.5">
                              <div className="font-bold text-slate-500 dark:text-slate-400">Cash</div>
                              {p.bankName && <div><span className="font-bold text-slate-400 dark:text-slate-500">Bank:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.bankName}</span></div>}
                              {p.accountNo && <div><span className="font-bold text-slate-400 dark:text-slate-500">A/C:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.accountNo}</span></div>}
                            </div>
                          );
                        }
                        if (p.bankName || p.accountNo) {
                          return (
                            <div className="flex flex-col gap-0.5">
                              {p.bankName && <div><span className="font-bold text-slate-400 dark:text-slate-500">Bank:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.bankName}</span></div>}
                              {p.accountNo && <div><span className="font-bold text-slate-400 dark:text-slate-500">A/C:</span> <span className="font-medium text-slate-700 dark:text-slate-300">{p.accountNo}</span></div>}
                            </div>
                          );
                        }
                        return null;
                      })();

                      return (
                        <div key={p.id} className="p-4 space-y-3 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[10px] font-black text-slate-500">
                                {index + 1}
                              </span>
                              <span className="font-bold text-slate-500">{formatDate(p.date)}</span>
                            </div>
                            <span className="px-2 py-0.5 rounded bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 text-[10px] font-bold">
                              {p.paymentMode?.paymentMode || `Mode #${p.paymentModeId}`}
                            </span>
                          </div>

                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500 font-bold">Amount (₹):</span>
                            <span className="font-black text-emerald-600 dark:text-emerald-400 font-mono">
                              ₹{Number(p.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                            </span>
                          </div>

                          {detailsContent && (
                            <div className="text-[11px] text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/50 p-2.5 rounded-lg border border-slate-100 dark:border-slate-800">
                              {detailsContent}
                            </div>
                          )}

                          {remarksText !== "-" && (
                            <div className="text-[11px] text-slate-500 italic">
                              <span className="font-bold text-slate-400">Remarks: </span>{remarksText}
                            </div>
                          )}

                          <div className="flex items-center justify-between border-t border-slate-100 dark:border-slate-800 pt-2 mt-2">
                            <div>
                              {p.attachmentPath ? (
                                <a
                                  href={`${API_CONFIG.baseUrl}/${p.attachmentPath.replace(/\\/g, "/")}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-[11px] text-primary-600 hover:text-primary-700 font-bold"
                                >
                                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  View Receipt
                                </a>
                              ) : (
                                <span className="text-[10px] text-slate-400 italic">No receipt</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleEditTransaction(p)}
                                className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/20 transition-all text-xs flex items-center gap-1 border border-blue-200/50 dark:border-blue-950/50"
                              >
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteTransaction(p)}
                                className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/20 transition-all text-xs flex items-center gap-1 border border-rose-200/50 dark:border-rose-950/50"
                              >
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Right Side: Form or Fully Paid Card */}
            <div className="lg:col-span-4">
              {Number(sellingMasterData.remainingReceivableAmount || 0) <= 0 &&
              !isEditingTransaction ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-6 text-center dark:border-emerald-900/30 dark:bg-emerald-950/20 sticky top-6 animate-in zoom-in duration-300">
                  <div className="flex flex-col items-center justify-center gap-3">
                    <i className="material-icons text-4xl text-emerald-500">
                      task_alt
                    </i>
                    <h3 className="text-sm font-black text-slate-800 dark:text-white uppercase tracking-tight">
                      Fully Received
                    </h3>
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest leading-relaxed">
                      This invoice has been fully received. No further payment
                      transactions are required.
                    </p>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-2">
                      To modify payments, click the edit or delete icons in the
                      transaction history table.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm dark:border-slate-800 dark:bg-slate-900 sticky top-6">
                  <div className="bg-slate-50/50 dark:bg-slate-800/50 p-4 border-b border-slate-100 dark:border-slate-800">
                    <h2 className="text-xs font-black text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-2">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 text-[10px] text-white">
                        {isEditingTransaction ? "✎" : "+"}
                      </span>
                      {isEditingTransaction ? "Edit Payment" : "Add Payment"}
                    </h2>
                  </div>

                  <form onSubmit={handleSubmitTransaction}>
                    <table className="w-full border-collapse">
                      <tbody>
                        {/* --- Payment Date --- */}
                        <tr className="border-b border-slate-100 dark:border-slate-800 flex flex-col sm:table-row">
                          <td className="w-full sm:w-1/3 py-2 sm:py-4 px-4 text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 bg-slate-50/50 dark:bg-slate-800/50 block sm:table-cell">
                            Date <span className="text-danger">*</span>
                          </td>
                          <td className="py-3 px-4 block sm:table-cell">
                            <DatePicker
                              value={transactionForm.date}
                              onChange={(val) => handleFormChange("date", val)}
                              error={formErrors.date}
                              placeholder="YYYY-MM-DD"
                              minDate={sellingMasterData?.date}
                            />
                          </td>
                        </tr>

                        {/* --- Payment Mode --- */}
                        <tr className="border-b border-slate-100 dark:border-slate-800 flex flex-col sm:table-row">
                          <td className="w-full sm:w-1/3 py-2 sm:py-4 px-4 text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 bg-slate-50/50 dark:bg-slate-800/50 block sm:table-cell">
                            Mode <span className="text-danger">*</span>
                          </td>
                          <td className="py-3 px-4 block sm:table-cell">
                            <SearchableDropdown
                              value={transactionForm.paymentModeId}
                              options={paymentModes}
                              placeholder="Select Mode"
                              onChange={(e) =>
                                handleFormChange(
                                  "paymentModeId",
                                  e.target.value,
                                )
                              }
                              error={formErrors.paymentModeId}
                            />
                          </td>
                        </tr>

                        {/* --- Amount (₹) --- */}
                        <tr className="border-b border-slate-100 dark:border-slate-800 flex flex-col sm:table-row">
                          <td className="w-full sm:w-1/3 py-2 sm:py-4 px-4 text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 bg-slate-50/50 dark:bg-slate-800/50 block sm:table-cell">
                            Amount (₹) <span className="text-danger">*</span>
                          </td>
                          <td className="py-3 px-4 block sm:table-cell">
                            <Input
                              type="number"
                              min="0"
                              value={transactionForm.amount}
                              onChange={(e) =>
                                handleAmountChange(e.target.value)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "-" || e.key === "e" || e.key === "E") {
                                  e.preventDefault();
                                }
                              }}
                              placeholder="0.00"
                              error={formErrors.amount}
                              className="font-bold font-mono"
                            />
                          </td>
                        </tr>

                        {/* --- Dynamic Payment Fields (Strategy B) --- */}
                        {(() => {
                          const selectedMode = paymentModes.find(m => String(m.value) === String(transactionForm.paymentModeId));
                          const sysKey = selectedMode ? (selectedMode.systemKey || "") : "";
                          if (!transactionForm.paymentModeId) return null;

                          return (
                            <tr className="bg-slate-50/30 dark:bg-slate-800/10 flex flex-col sm:table-row">
                              <td colSpan="2" className="py-4 px-4 w-full block sm:table-cell">
                                <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 space-y-4 shadow-sm">
                                  <div className="text-[10px] font-black text-primary-600 dark:text-primary-400 uppercase tracking-widest flex items-center gap-1.5 border-b border-slate-100 dark:border-slate-800 pb-2 mb-1">
                                    <i className="material-icons text-sm">info</i>
                                    {selectedMode?.label || "Payment"} Details
                                  </div>

                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {/* Bank Name — Mandatory for ALL modes */}
                                    <div className="space-y-1">
                                      <Input
                                        label={
                                          sysKey === "cheque"
                                            ? "Drawn On (Bank) *"
                                            : "Bank Name *"
                                        }
                                        type="text"
                                        value={transactionForm.bankName}
                                        onChange={(e) => handleFormChange("bankName", e.target.value)}
                                        placeholder={sysKey === "cheque" ? "Bank name" : "e.g., SBI, HDFC"}
                                        error={formErrors.bankName}
                                        maxLength={80}
                                        labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                                      />
                                    </div>

                                    {/* Account Number — Cash (Optional) | Bank (Mandatory) */}
                                    {(sysKey === "cash" || sysKey === "bank") && (
                                      <div className="space-y-1">
                                        <Input
                                          label={sysKey === "bank" ? "Account Number *" : "Account Number"}
                                          type="text"
                                          value={transactionForm.accountNo}
                                          onChange={(e) => handleFormChange("accountNo", e.target.value)}
                                          placeholder="Account number"
                                          error={formErrors.accountNo}
                                          maxLength={20}
                                          labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                                        />
                                      </div>
                                    )}

                                    {/* IFSC Code — Bank only (Mandatory) */}
                                    {sysKey === "bank" && (
                                      <div className="space-y-1">
                                        <Input
                                          label="IFSC Code *"
                                          type="text"
                                          value={transactionForm.ifscCode}
                                          onChange={(e) => handleFormChange("ifscCode", e.target.value)}
                                          placeholder="e.g., SBIN0001234"
                                          error={formErrors.ifscCode}
                                          maxLength={14}
                                          labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                                        />
                                      </div>
                                    )}

                                    {/* UTR / Ref No — Bank only (Mandatory) */}
                                    {sysKey === "bank" && (
                                      <div className="space-y-1">
                                        <Input
                                          label="UTR / Reference Number *"
                                          type="text"
                                          value={transactionForm.transactionRefNo}
                                          onChange={(e) => handleFormChange("transactionRefNo", e.target.value)}
                                          placeholder="UTR reference number"
                                          error={formErrors.transactionRefNo}
                                          labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                                        />
                                      </div>
                                    )}

                                    {/* UPI ID (VPA) — UPI only (Mandatory) */}
                                    {sysKey === "upi" && (
                                      <div className="space-y-1">
                                        <Input
                                          label="UPI ID (VPA) *"
                                          type="text"
                                          value={transactionForm.vpa}
                                          onChange={(e) => handleFormChange("vpa", e.target.value)}
                                          placeholder="e.g., username@upi"
                                          error={formErrors.vpa}
                                          labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                                        />
                                      </div>
                                    )}

                                    {/* Transaction ID — UPI only (Mandatory) */}
                                    {sysKey === "upi" && (
                                      <div className="space-y-1">
                                        <Input
                                          label="Transaction ID *"
                                          type="text"
                                          value={transactionForm.transactionRefNo}
                                          onChange={(e) => handleFormChange("transactionRefNo", e.target.value)}
                                          placeholder="UPI reference / transaction ID"
                                          error={formErrors.transactionRefNo}
                                          labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                                        />
                                      </div>
                                    )}

                                    {/* Cheque Number — Cheque only (Mandatory) */}
                                    {sysKey === "cheque" && (
                                      <div className="space-y-1">
                                        <Input
                                          label="Cheque Number *"
                                          type="text"
                                          value={transactionForm.chequeNo}
                                          onChange={(e) => handleFormChange("chequeNo", e.target.value)}
                                          placeholder="Cheque number"
                                          error={formErrors.chequeNo}
                                          maxLength={10}
                                          labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                                        />
                                      </div>
                                    )}

                                    {/* Cheque Date — Cheque only (Mandatory) */}
                                    {sysKey === "cheque" && (
                                      <div className="space-y-1">
                                        <DatePicker
                                          label="Cheque Date *"
                                          value={transactionForm.chequeDate}
                                          onChange={(val) => handleFormChange("chequeDate", val)}
                                          error={formErrors.chequeDate}
                                          labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block"
                                        />
                                      </div>
                                    )}

                                    {/* Attachment File Upload & Preview */}
                                    <div className="space-y-1 sm:col-span-2">
                                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                                        Attachment
                                      </label>
                                      {transactionForm.attachment || (transactionForm.attachmentPath && transactionForm.attachmentPath !== "") ? (
                                        <div className="flex items-center justify-between py-1 px-2.5 bg-slate-50/40 dark:bg-slate-900/40 border border-slate-200/80 dark:border-slate-800 rounded-md shadow-sm animate-in fade-in duration-200">
                                          <span className="text-[11px] font-bold text-slate-700 dark:text-slate-200 truncate pr-2" title={
                                            transactionForm.attachment
                                              ? transactionForm.attachment.name
                                              : transactionForm.attachmentPath.split(/[\\/]/).pop()
                                          }>
                                            {transactionForm.attachment
                                              ? transactionForm.attachment.name
                                              : transactionForm.attachmentPath.split(/[\\/]/).pop()}
                                          </span>
                                          <div className="flex items-center gap-1.5 flex-shrink-0">
                                            <button
                                              type="button"
                                              onClick={() => {
                                                if (transactionForm.attachment) {
                                                  const fileUrl = URL.createObjectURL(transactionForm.attachment);
                                                  window.open(fileUrl, "_blank");
                                                } else if (transactionForm.attachmentPath) {
                                                  window.open(`${API_CONFIG.baseUrl}/${transactionForm.attachmentPath.replace(/\\/g, "/")}`, "_blank");
                                                }
                                              }}
                                              className="flex h-[26px] w-[26px] items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm active:scale-95"
                                              title="View Attachment"
                                            >
                                              <i className="material-icons text-amber-700 dark:text-amber-500" style={{ fontSize: "14px" }}>visibility</i>
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setTransactionForm(prev => ({
                                                  ...prev,
                                                  attachment: null,
                                                  attachmentPath: ""
                                                }));
                                                const fileInput = document.getElementById("payment-attachment-input");
                                                if (fileInput) fileInput.value = "";
                                              }}
                                              className="flex h-[26px] w-[26px] items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm active:scale-95"
                                              title="Remove Attachment"
                                            >
                                              <i className="material-icons text-rose-500 dark:text-rose-400" style={{ fontSize: "14px" }}>close</i>
                                            </button>
                                          </div>
                                        </div>
                                      ) : (
                                        <input
                                          id="payment-attachment-input"
                                          type="file"
                                          onChange={(e) => handleFormChange("attachment", e.target.files[0])}
                                          accept="application/pdf,image/jpeg,image/png"
                                          className="block w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 cursor-pointer"
                                        />
                                      )}
                                      <p className="text-[10px] text-rose-500 font-semibold mt-1">
                                        * Only PDF, JPG, PNG. Maximum Size: 5 MB
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                            );
                          })()}

                        {/* --- Remarks --- */}
                        <tr className="border-b border-slate-100 dark:border-slate-800 flex flex-col sm:table-row">
                          <td className="w-full sm:w-1/3 py-2 sm:py-4 px-4 text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 bg-slate-50/50 dark:bg-slate-800/50 block sm:table-cell">
                            Remarks
                          </td>
                          <td className="py-3 px-4 block sm:table-cell">
                            <TextArea
                              value={transactionForm.remarks}
                              onChange={(e) =>
                                handleFormChange("remarks", e.target.value)
                              }
                              placeholder="Add remarks (optional)"
                              error={formErrors.remarks}
                              className="font-medium"
                              rows={2}
                            />
                          </td>
                        </tr>

                        {/* --- Active Status (Only in Edit Mode) --- */}
                        {isEditingTransaction && (
                          <tr className="flex flex-col sm:table-row">
                            <td className="w-full sm:w-1/3 py-2 sm:py-4 px-4 text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 bg-slate-50/50 dark:bg-slate-800/50 block sm:table-cell">
                              Status
                            </td>
                            <td className="py-3 px-4 block sm:table-cell">
                              <label className="flex cursor-pointer items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={transactionForm.isActive}
                                  onChange={(e) =>
                                    handleFormChange(
                                      "isActive",
                                      e.target.checked,
                                    )
                                  }
                                  className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-600/20"
                                />
                                <span className="text-[10px] font-bold text-slate-700 dark:text-slate-300 uppercase">
                                  Active
                                </span>
                              </label>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    <div className="bg-slate-50 dark:bg-slate-800/30 p-4 flex gap-2">
                      <Button
                        type="submit"
                        className="flex-1"
                        isLoading={isSubmittingTransaction}
                      >
                        {isEditingTransaction
                          ? "Update Payment"
                          : "Save Payment"}
                      </Button>
                      {isEditingTransaction && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={resetTransactionForm}
                          className="text-slate-500"
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SalesPaymentDetailPage;
